import { useCallback } from 'react'
import { graphql, useMutation, useRefetchableFragment } from 'react-relay'

import {
  type useVerificationPhotos_profile$data,
  type useVerificationPhotos_profile$key
} from '@/__generated__/useVerificationPhotos_profile.graphql'
import { type useVerificationPhotosCreatePhotoMutation } from '@/__generated__/useVerificationPhotosCreatePhotoMutation.graphql'
import { type UseVerificationPhotosQuery } from '@/__generated__/UseVerificationPhotosQuery.graphql'

export type UseVerificationPhotosEdge = NonEmpty<
  ElementOf<NonEmpty<NonEmpty<useVerificationPhotos_profile$data['user']>['verificationPhotos']['edges']>>
>

export const useVerificationPhotos = (profileRef: useVerificationPhotos_profile$key | null | undefined) => {
  // noinspection GraphQLUnresolvedReference
  const [profileData, refetchVerificationPhotos] = useRefetchableFragment<
    UseVerificationPhotosQuery,
    useVerificationPhotos_profile$key
  >(
    graphql`
      fragment useVerificationPhotos_profile on Profile
      @argumentDefinitions(after: { type: "Cursor" }, first: { type: "Int", defaultValue: 100 })
      @refetchable(queryName: "UseVerificationPhotosQuery") {
        user {
          verificationPhotos(after: $after, first: $first)
            @connection(key: "useVerificationPhotos_profile_verificationPhotos") {
            __id
            edges {
              cursor
              node {
                createdAt
                description
                rowId
                sequence
                storageObject {
                  blurhash
                  bucket
                  key
                }
              }
            }
          }
        }
      }
    `,
    profileRef
  )
  const [_createVerificationPhoto, isCreatingVerificationPhoto] = useMutation<useVerificationPhotosCreatePhotoMutation>(
    graphql`
      mutation useVerificationPhotosCreatePhotoMutation($connections: [ID!]!, $input: CreatePhotoInput!) {
        createPhoto(input: $input) {
          photoEdge(orderBy: [SEQUENCE_ASC]) @appendEdge(connections: $connections) {
            cursor
            node {
              createdAt
              description
              id
              rowId
              sequence
              storageObject {
                blurhash
                bucket
                key
              }
            }
          }
        }
      }
    `
  )
  const createVerificationPhoto = useCallback(
    (storageObjectId: string): Promise<useVerificationPhotosCreatePhotoMutation['response']> =>
      new Promise((resolve, reject) =>
        _createVerificationPhoto({
          onCompleted: (response, errors) => {
            if (errors) {
              reject(errors[0])
            } else {
              resolve(response)
            }
          },
          onError: (error) => reject(error),
          variables: {
            connections: profileData?.user?.verificationPhotos?.__id
              ? [profileData?.user?.verificationPhotos?.__id]
              : [],
            input: {
              photo: {
                storageObjectId,
                type: 'VERIFICATION'
              }
            }
          }
        })
      ),
    [_createVerificationPhoto, profileData?.user?.verificationPhotos?.__id]
  )

  return {
    createVerificationPhoto,
    isCreatingVerificationPhoto,
    verificationPhotosConnection: profileData?.user?.verificationPhotos,
    refetchVerificationPhotos
  }
}
